import { ApplicationRef, ComponentRef, DOCUMENT, Injectable, Renderer2, RendererFactory2, Type, createComponent, inject } from '@angular/core';

import { ModalContainerComponent } from '../components/modal-container/modal-container.component';
import { ModalRef } from '../models/modal-ref.model';
import { Modal } from '../models/modal.model';

@Injectable()
export class ModalService {
  private appRef = inject(ApplicationRef);
  private rendererFactory = inject(RendererFactory2);
  private document = inject(DOCUMENT);

  private renderer: Renderer2;
  private modalContainer: HTMLElement;

  constructor() {
    this.setupModalContainerFactory();
  }

  open<T extends Modal>(
    component: Type<T>,
    inputs?: any,
    size?: 'small' | 'large' | 'xl' | 'fullscreen',
    options?: { noBg?: boolean; headerHeight?: 'normal' | 'small'; rounded?: 'normal' | 'small'; keepOpen?: boolean }
  ): ModalRef {
    this.setupModalContainerDiv();

    const containerRef = this.createModalContainer(size, options);
    const modalComponentRef = containerRef.instance.createModal(component);
    try {
      modalComponentRef.instance.onInjectInputs(inputs || {});
    } finally {
      this.appRef.attachView(containerRef.hostView);
      containerRef.changeDetectorRef.detectChanges();
    }

    return new ModalRef(containerRef, modalComponentRef);
  }

  private createModalContainer(
    size?: 'small' | 'large' | 'xl' | 'fullscreen',
    options?: { noBg?: boolean; headerHeight?: 'normal' | 'small'; rounded?: 'normal' | 'small'; keepOpen?: boolean }
  ): ComponentRef<ModalContainerComponent> {
    const containerRef = createComponent(ModalContainerComponent, {
      environmentInjector: this.appRef.injector,
      hostElement: this.modalContainer
    });
    containerRef.instance.size = size || 'small';
    containerRef.instance.options = {
      noBg: options?.noBg || false,
      headerHeight: options?.headerHeight || 'normal',
      rounded: options?.rounded || 'normal',
      keepOpen: options?.keepOpen || false
    };
    return containerRef;
  }

  private setupModalContainerDiv(): void {
    this.modalContainer = this.renderer.createElement('div');
    this.renderer.appendChild(this.document.body, this.modalContainer);
  }

  private setupModalContainerFactory(): void {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }
}
